import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPaperclip } from 'react-icons/fa';
import { useAppDispatch } from '@app/store/store';
import { Order } from '../../../types/interfaces';
import { InputField } from '@shared/ui/input-field';
import { partialUpdateOrder } from '@entities/order/model';
import { MyModal } from '@shared/ui/my-modal';
import { Button } from '@shared/ui/button';
import { apiClient } from '../../../api/instance';
import { setErrorNotification } from '../../../app/store/notifications';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
};

// Какие поля могут принимать файл-вложение.
type AttachField = 'confidant_number' | 'invoice_url';

export function UpdateSelfDeliveryInfo({ isOpen, onClose, order }: Props) {
  // Заметка про дефолты: раньше тут стоял useForm с defaultValues,
  // но MyModal не размонтирует контент при isOpen=false, а только
  // прячет — поэтому состояние формы переживало закрытие модалки и
  // при следующем открытии в поле «Номер или фото накладной» уже
  // лежал URL от предыдущей (не сохранённой) попытки. Чтобы это
  // исправить, сбрасываем форму на open/close через reset() (см.
  // useEffect ниже).
  const defaults = {
    confidant_name: order.confidant_name,
    confidant_number: order.confidant_number,
    courier_service: order.courier_service,
    invoice_url: order.invoice_url,
  };
  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues: defaults,
  });

  const dispatch = useAppDispatch();
  const isConfidant = order.self_delivery_type === 'confidant';

  // Отложенный upload: пока пользователь не нажал «Сохранить», файл
  // НИКУДА не отправляется — он лежит только в локальном state.
  // Так нет «осиротевших» файлов в storage при отмене и нет иллюзии
  // авто-сохранения (поле invoice_url не подменяется ссылкой раньше
  // времени).
  const [pendingFiles, setPendingFiles] = useState<
    Partial<Record<AttachField, File>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Сброс формы и pending-файлов при открытии/закрытии модалки —
  // см. комментарий про MyModal выше.
  useEffect(() => {
    if (isOpen) {
      reset(defaults);
      setPendingFiles({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order.id]);

  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await apiClient.post('/files/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.file;
    } catch {
      dispatch(setErrorNotification('Ошибка при загрузке файла'));
      return null;
    }
  };

  // Хранит File и подменяет видимое значение в поле на имя файла —
  // чтобы пользователь видел что именно прикреплено. Сам upload
  // произойдёт только в onSubmit.
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: AttachField
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFiles((prev) => ({ ...prev, [fieldName]: file }));
    setValue(fieldName, file.name, { shouldDirty: true });
    // сбрасываем input, чтобы можно было повторно выбрать тот же файл
    e.target.value = '';
  };

  const onSubmit = async (data: Record<string, unknown>) => {
    setIsSubmitting(true);
    try {
      // Сначала аплоадим все pending-файлы и подменяем соответствующие
      // поля в payload на URL'ы; то что пользователь ввёл руками
      // (без файла) остаётся как есть.
      const payload: Record<string, unknown> = { ...data };
      for (const field of Object.keys(pendingFiles) as AttachField[]) {
        const file = pendingFiles[field];
        if (!file) continue;
        const url = await uploadFile(file);
        if (!url) {
          // Не делаем partialUpdate если файл не загрузился — иначе в
          // заказе окажется имя файла («photo.jpg») вместо ссылки.
          return;
        }
        payload[field] = url;
      }
      dispatch(partialUpdateOrder({ data: payload, id: order.id ?? 0 }));
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Явно дёргаем reset на отмене — чтобы при следующем открытии
    // в инпуте не «всплыло» имя файла, которое мы туда положили.
    reset(defaults);
    setPendingFiles({});
    onClose();
  };

  // Скрытый input + label-«скрепка», который теперь рендерится
  // через InputField.rightSlot — позиционирование корректное
  // относительно самого input, а не относительно блока с лейблом.
  const renderAttachSlot = (fieldName: AttachField) => {
    const inputId = `update_self_delivery_file_${fieldName}`;
    return (
      <>
        <input
          type="file"
          id={inputId}
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleFileChange(e, fieldName)}
        />
        <label
          htmlFor={inputId}
          className="cursor-pointer flex items-center"
          title="Прикрепить фото или файл"
        >
          <FaPaperclip className="text-xl text-red-600" />
        </label>
      </>
    );
  };

  return (
    <MyModal isOpen={isOpen} onClose={handleCancel}>
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4">
          Редактирование информации о самовывозе
        </h3>
        <form onSubmit={handleSubmit(onSubmit)}>
          {isConfidant ? (
            <>
              <InputField
                label="ФИО доверенного лица"
                register={register}
                inputName="confidant_name"
              />
              <InputField
                label="Номер доверенности или фото"
                register={register}
                inputName="confidant_number"
                className="pr-10"
                rightSlot={renderAttachSlot('confidant_number')}
              />
              {pendingFiles.confidant_number && (
                <p className="text-xs text-gray-500 break-all -mt-2 mb-2">
                  Файл выбран: {pendingFiles.confidant_number.name} (будет
                  загружен после нажатия «Сохранить»)
                </p>
              )}
            </>
          ) : (
            <>
              <InputField
                label="Курьерский сервис"
                register={register}
                inputName="courier_service"
              />
              <InputField
                label="Номер или фото накладной"
                register={register}
                inputName="invoice_url"
                className="pr-10"
                rightSlot={renderAttachSlot('invoice_url')}
              />
              {pendingFiles.invoice_url && (
                <p className="text-xs text-gray-500 break-all -mt-2 mb-2">
                  Файл выбран: {pendingFiles.invoice_url.name} (будет
                  загружен после нажатия «Сохранить»)
                </p>
              )}
            </>
          )}
          <div className="flex gap-3 justify-end mt-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Сохранение…' : 'Сохранить'}
            </Button>
            <Button
              variant="light"
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
            >
              Отмена
            </Button>
          </div>
        </form>
      </div>
    </MyModal>
  );
}
